import React, { useEffect, useRef } from 'react';
import { Calendar } from '@framework-controls/calendar';
import { DateTime } from '@reynolds/datetime';
import classNames from 'classnames';
import css from './CalendarSelectCal.module.scss';
import { invalidDates } from 'src/models/RMTimes/RMTimes';
import { IView, LoadingProgress } from '@framework-controls/core';
import { ascCtaInteraction, ascPageview, ascElementConfiguration } from 'src/helpers/EventHelper/EventHelper';
import { VehicleRec, ascCtaInteractionInput, ascPageviewInput, ascElementConfigurationInput } from 'src/models';
import { ComponentMapIds } from 'src/components/map';
import { IAppointmentState } from 'src/store/states';
import { THEME } from 'src/constants/ThemeNames';

interface IProps {
    id: string;
    mainPaneViewOrder: IView[];
    onDateChange: (date: DateTime) => void;
    dateSelected: DateTime;
    invalidDates: invalidDates[];
    onMonthChange: (date: DateTime) => void;
    locale: string;
    clearInvalid: () => void;
    getInvalid: (specProfile: string, selectedDate?: string, selectedAdv?: undefined, recallSelected?: boolean) => void;
    specProfile: string;
    vehicles: VehicleRec[];
    selectedVehicle?: string;
    pageViewed: React.MutableRefObject<boolean>;
    theme: string;
    appointment?: IAppointmentState
}

const CalendarSelectCal = (props: IProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [dateSelect, setDateSelect] = React.useState<DateTime>(props.dateSelected);
    // grabs numerical value of todays date
    const today = DateTime.now().get('date');
    const invalidDates = [] as any[];
    for (let i = 0; i < props.invalidDates.length; i++) {
        invalidDates[i] = new DateTime(new Date(props.invalidDates[i].date));
    }
    // goes backwards until first of the month and adds dates to the invalid dates array
    for (let i=0; i < today; i++) {
        invalidDates.unshift(DateTime.now().addDays(-(1+i)));
    }
    const skipDate = invalidDates;
    const onChange = React.useCallback((date: DateTime) => {
        const testA = date.toDateString();
        //  Exit if the selected date is one of the unavailable Days
        for (let i = 0; i < skipDate.length; i++) {
            const testB = skipDate[i].toDateString();
            if (testA === testB) {
                return;
            }
        }
        setDateSelect(date);
        props.onDateChange(date);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: testA,
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'other_buttons',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, '', props.vehicles, props.selectedVehicle);
    }, [props.onDateChange, skipDate, props.vehicles, props.selectedVehicle]);

    const onMonthChange = React.useCallback((date: DateTime) => {
        props.onMonthChange(date);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: '',
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'other_buttons',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, '', props.vehicles, props.selectedVehicle);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: '',
            elementType: 'scheduling_tool',
            elementColor: '',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, '');
    }, [props.onMonthChange, props.selectedVehicle, props.vehicles]);

    let calLocale: 'en-US' | 'en-CA' | 'fr-CA' = 'en-US';
    if (props.locale === 'en-CA') {
        calLocale = 'en-CA';
    }
    if (props.locale === 'fr-CA') {
        calLocale = 'fr-CA';
    }

    useEffect(() => {
        if (props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].viewId === ComponentMapIds.CalendarSelect) {
            const ascPageViewInp: ascPageviewInput = {
                pageType: 'service',
                errorCode: '',
                itemResults: '',
            };
            ascPageview(ascPageViewInp, props.vehicles, props.selectedVehicle);
            props.pageViewed.current = true;
        }
    }, [props.mainPaneViewOrder, props.vehicles, props.selectedVehicle, props.pageViewed]);

    return (
        <div className={classNames(css.calendar, { [css.calendarAudi]: props.theme === THEME.AUDI })} ref={containerRef}>
            {props.invalidDates.length === 0 ?
                <LoadingProgress cssClasses={{ spinner: css.progressColor }} size={100} /> : null}
            <Calendar
                id='calendar'
                date={dateSelect}
                onChange={onChange}
                disablePast={true}
                dayLabelType={props.theme === 'chevrolet' || props.theme === 'honda' ||
                    props.theme === 'buick' ? 'char' : 'abbr'}
                size='full'
                unavailableDays={skipDate}
                locale={calLocale}
                getOutlinedDays={onMonthChange}
            />
        </div>
    );
};

export default CalendarSelectCal;